import type { AxiosInstance } from 'axios';
import type {
  CancelInvitationRequest,
  ListInvitationsRequest,
  ReplyInvitationRequest,
  InvitationResponse,
  ListLiveUserRoomsRequest,
  LiveUserRoomResponse,
  GetRoomByIdRequest,
  RoomResponse,
  RoomMemberResponse,
  GetInitialContextRequest,
  GetInitialContextResponse,
  GetMessagesByCursorRequest,
  GetMessagesByCursorResponse,
  ForwardMessageRequest,
  SendMessageRequest,
  MessageResponse,
  SendAttachmentMessageResponse,
  MarkMessagesAsReadRequest,
  MarkMessagesAsReadResponse,
  UpdateMemberInRoomRequest,
  ChatWebserviceCompanyUsageRequest,
  ChatWebserviceCompanyUsageResponse,
} from './contracts';

/**
 * Gateway for interacting with the Chat Webservice API.
 */
export class ChatWebserviceGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Lists chat invitations for a user within a company.
   * @param params - The user, company, and invitation status filters.
   * @returns The matching chat invitations.
   */
  async listInvitations(params: ListInvitationsRequest): Promise<InvitationResponse[]> {
    const { data } = await this.httpClient.get<InvitationResponse[]>(`${this.baseUrl}/invitations`, { params });
    return data;
  }

  /**
   * Replies to a chat invitation.
   * @param payload - The invitation identifier to reply to.
   * @returns The updated chat invitations.
   */
  async replyInvitation(payload: ReplyInvitationRequest): Promise<InvitationResponse[]> {
    const { data } = await this.httpClient.put<InvitationResponse[]>(`${this.baseUrl}/invitations/reply`, payload);
    return data;
  }

  /**
   * Cancels a chat invitation for a room.
   * @param roomId - The chat room identifier.
   * @param payload - The delegator identifier used to cancel the invitation.
   */
  async cancelInvitation(roomId: number, payload: CancelInvitationRequest): Promise<void> {
    await this.httpClient.delete(`${this.baseUrl}/invitations/chat/${roomId}`, {
      data: payload,
    });
  }

  /**
   * Lists live rooms assigned to a user.
   * @param params - The recipient, company, and open-status filters.
   * @returns The live room entries for the user.
   */
  async listLiveUserRooms(params: ListLiveUserRoomsRequest): Promise<LiveUserRoomResponse[]> {
    const { data } = await this.httpClient.get<LiveUserRoomResponse[]>(`${this.baseUrl}/member/rooms/live`, { params });
    return data;
  }

  /**
   * Retrieves a chat room by its identifier.
   * @param id - The unique identifier of the room.
   * @param params - Optional query parameters for scoping and member inclusion.
   * @returns The chat room data.
   */
  async getRoomById(id: number, params: GetRoomByIdRequest = {}): Promise<RoomResponse> {
    const { data } = await this.httpClient.get<RoomResponse>(`${this.baseUrl}/room/${id}`, { params });
    return data;
  }

  /**
   * Fetches the initial message context for a room member, centered around unread messages.
   * @param params - The room, member, and pagination filters.
   * @returns The initial page of messages with unread metadata.
   */
  async getInitialContext(params: GetInitialContextRequest): Promise<GetInitialContextResponse> {
    const { data } = await this.httpClient.get<GetInitialContextResponse>(`${this.baseUrl}/message/initial-context`, {
      params,
    });
    return data;
  }

  /**
   * Lists messages in a room using cursor-based pagination.
   * @param params - The room, cursor, and association filters.
   * @returns A page of messages with cursor metadata.
   */
  async getMessagesByCursor(params: GetMessagesByCursorRequest): Promise<GetMessagesByCursorResponse> {
    const { data } = await this.httpClient.get<GetMessagesByCursorResponse>(`${this.baseUrl}/message/cursor`, {
      params,
    });
    return data;
  }

  /**
   * Sends a chat message to a room.
   * @param payload - The message content and routing metadata.
   * @returns The created message.
   */
  async sendMessage(payload: SendMessageRequest): Promise<MessageResponse> {
    const { data } = await this.httpClient.post<MessageResponse>(`${this.baseUrl}/message`, payload);
    return data;
  }

  /**
   * Creates a forwarded chat message.
   * @param payload - The message content and forwarding metadata.
   * @returns The created message.
   */
  async forwardMessage(payload: ForwardMessageRequest): Promise<MessageResponse> {
    const { data } = await this.httpClient.post<MessageResponse>(`${this.baseUrl}/message/forward`, payload);
    return data;
  }

  /**
   * Sends a chat message that includes attachments.
   * @param payload - The message content, attachments, and routing metadata.
   * @returns The created message wrapped in a response object.
   */
  async sendAttachmentMessage(payload: SendMessageRequest): Promise<SendAttachmentMessageResponse> {
    const { data } = await this.httpClient.post<SendAttachmentMessageResponse>(
      `${this.baseUrl}/message/attachment`,
      payload,
    );
    return data;
  }

  /**
   * Marks specific messages as read for a room member.
   * @param payload - The room, member, and message identifiers to update.
   * @returns The updated message status entries.
   */
  async markMessagesAsRead(payload: MarkMessagesAsReadRequest): Promise<MarkMessagesAsReadResponse> {
    const { data } = await this.httpClient.patch<MarkMessagesAsReadResponse>(`${this.baseUrl}/message-status`, payload);
    return data;
  }

  /**
   * Marks all messages in a room as read for a given member.
   * @param memberId - The chat room member identifier.
   * @param roomId - The chat room identifier.
   * @returns The API response payload.
   */
  async markAllRoomMessagesAsRead(memberId: number, roomId: number): Promise<unknown> {
    const { data } = await this.httpClient.patch(
      `${this.baseUrl}/message-status/mark-all-as-read/${memberId}/${roomId}`,
    );
    return data;
  }

  /**
   * Updates a room member's attributes.
   * @param memberId - The chat room member identifier.
   * @param payload - The member fields to update.
   * @returns The updated room member.
   */
  async updateMemberInRoom(memberId: number, payload: UpdateMemberInRoomRequest): Promise<RoomMemberResponse> {
    const { data } = await this.httpClient.patch<RoomMemberResponse>(`${this.baseUrl}/member/${memberId}`, payload);
    return data;
  }

  /**
   * Fetches aggregated chat and email usage for a company.
   * @param companyId - The company identifier.
   * @param params - Optional date range filters.
   * @returns The aggregated chat and email usage totals.
   */
  async getCompanyUsage(
    companyId: string,
    params: ChatWebserviceCompanyUsageRequest = {},
  ): Promise<ChatWebserviceCompanyUsageResponse> {
    const { data } = await this.httpClient.get<ChatWebserviceCompanyUsageResponse>(
      `${this.baseUrl}/companies/${companyId}/usage`,
      { params },
    );
    return data;
  }
}

export type {
  CancelInvitationRequest,
  ChatWebserviceChatUsageProvidersResponse,
  ChatWebserviceChatUsageResponse,
  ChatWebserviceCompanyUsageEntryResponse,
  ChatWebserviceCompanyUsageRequest,
  ChatWebserviceCompanyUsageResponse,
  ChatWebserviceEmailUsageProvidersResponse,
  ChatWebserviceEmailUsageResponse,
  GetInitialContextRequest,
  GetInitialContextResponse,
  GetMessagesByCursorRequest,
  GetMessagesByCursorResponse,
  GetRoomByIdRequest,
  ForwardMessageDestination,
  ForwardMessageForwardingMetadata,
  ForwardMessageMetadata,
  ForwardMessageRequest,
  InvitationResponse,
  ListInvitationsRequest,
  ListLiveUserRoomsRequest,
  LiveUserRoomResponse,
  MarkMessagesAsReadRequest,
  MarkMessagesAsReadResponse,
  MessageAttachment,
  MessageMetadata,
  MessageReactionResponse,
  MessageReplyContext,
  MessageResponse,
  MessageStatusResponse,
  ReplyInvitationRequest,
  RoomActiveDates,
  RoomMemberResponse,
  RoomResponse,
  RoomTagResponse,
  SendAttachmentMessageResponse,
  SendMessageRequest,
  UpdateMemberInRoomRequest,
} from './contracts';
